import { useEffect, useState } from 'react';

import { getCallScribeCall } from 'services/callScribe';
import { ICallScribeCallListItem } from 'services/callScribe/types';

const NO_AUTOMATED_DISCLOSURE = 2;

export const CALL_META_RETRY_MS: readonly number[] = [1000, 2000, 4000, 8000, 15000];

export const isNoAutomatedDisclosure = (mode: unknown): boolean =>
  mode === NO_AUTOMATED_DISCLOSURE ||
  (typeof mode === 'string' &&
    (mode.trim() === String(NO_AUTOMATED_DISCLOSURE) ||
      mode.trim().toLowerCase() === 'noautomateddisclosure'));

export type ICallMeta = Pick<
  ICallScribeCallListItem,
  'consentMode' | 'crmSourceId' | 'companyExternalId'
>;

const retryDelay = (attempt: number): number =>
  CALL_META_RETRY_MS[Math.min(attempt, CALL_META_RETRY_MS.length - 1)];

export const useCallMeta = (callId: number | undefined, enabled: boolean): ICallMeta | undefined => {
  const [meta, setMeta] = useState<{ callId: number; value: ICallMeta } | undefined>(undefined);

  useEffect(() => {
    if (!enabled || typeof callId !== 'number' || meta?.callId === callId) return undefined;
    let cancelled = false;
    let timer: number | undefined;

    const read = (attempt: number): void => {
      void getCallScribeCall(callId)
        .then(res => (res?.data ? res.data : Promise.reject(new Error('No call in the answer'))))
        .then(call => {
          if (cancelled) return;
          setMeta({
            callId,
            value: {
              consentMode: call.consentMode,
              crmSourceId: call.crmSourceId,
              companyExternalId: call.companyExternalId,
            },
          });
        })
        .catch(() => {
          if (cancelled) return;
          timer = window.setTimeout(() => { read(attempt + 1); }, retryDelay(attempt));
        });
    };

    read(0);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [callId, enabled, meta?.callId]);

  return meta && meta.callId === callId ? meta.value : undefined;
};
